import api from '@shared/api';

import {
    BalanceBundle,
    BalanceBundleList,
    BalanceBundleListResponse,
    BalancePayedResponse,
    DeclarationsBundle,
    DeclarationsStats,
    GapesBundle,
    GapesCntSum,
    GapesCount,
    GapesDeductionPercent,
    GapesSum,
    InvoicesBundle,
    InvoiceStats,
    LegalPersonBundle,
    ManagementSummary,
    ManagementSummaryPeriodParams,
} from './types';

const FISCAL_PATH = 'tax-fiscal-data';
const ACCOUNTING_PATH = 'accounting';

const getPeriodParams = ({year, month}: ManagementSummaryPeriodParams) => ({
    year,
    ...(month && {month}),
});

const getPeriodString = (year: number, month: number) =>
    `${year}${month.toString().padStart(2, '0')}`;

const getPeriods = (year: number) =>
    Array.from({length: 12}, (_, index) => getPeriodString(year, index + 1));

const fetchNullable = async <T>(request: Promise<{data: T}>): Promise<T | null> => {
    try {
        return (await request).data;
    } catch (error) {
        console.error(error);
        return null;
    }
};

const fetchUsers = async (params: ManagementSummaryPeriodParams) => {
    const data = await fetchNullable<LegalPersonBundle<number | null>>(
        api.get(
            `${FISCAL_PATH}/legal_person/aggregate/getLegalPersonStatBy${
                params.month ? 'Month' : 'Year'
            }`,
            {params: getPeriodParams(params)},
        ),
    );

    return data ?? {active: null, total: null};
};

const fetchDeclarations = async (params: ManagementSummaryPeriodParams) => {
    const createUrl = (name: string) =>
        `${FISCAL_PATH}/declarations/aggregate/get${name}For${params.month ? 'Month' : 'Year'}`;

    const [filled, notFilled, zero, withGap, returned, pay] = await Promise.all(
        ['Filled', 'NotFilled', 'Zero', 'WithGap', 'Return', 'Pay'].map((name) =>
            fetchNullable<DeclarationsStats>(
                api.get(createUrl(name), {params: getPeriodParams(params)}),
            ),
        ),
    );

    return {
        filled: filled?.decCnt ?? filled?.cnt ?? null,
        notFilled: notFilled?.decCnt ?? filled?.cnt ?? null,
        zero: zero?.decCnt ?? filled?.cnt ?? null,
        withGap: withGap?.decCnt ?? filled?.cnt ?? null,
        return: returned?.decCnt ?? null,
        pay: pay?.decCnt ?? null,
    } as DeclarationsBundle;
};

const fetchInvoices = async (params: ManagementSummaryPeriodParams) => {
    const fetchByType = (docType: string) =>
        fetchNullable<InvoiceStats>(
            api.get(`${FISCAL_PATH}/get${params.month ? 'Monthly' : 'Yearly'}Stat`, {
                params: {
                    docType,
                    ...getPeriodParams(params),
                },
            }),
        );

    const [total, annulling, corrective] = await Promise.all([
        fetchNullable<InvoiceStats>(
            api.get(`${FISCAL_PATH}/get${params.month ? 'Monthly' : 'Yearly'}Data`, {
                params: getPeriodParams(params),
            }),
        ),
        fetchByType('ANNULLING'),
        fetchByType('CORRECTIVE_BASIC'),
    ]);

    return {
        total: total?.invoiceCnt ?? null,
        annulling: annulling?.invoiceCnt ?? null,
        corrective: corrective?.invoiceCnt ?? null,
    } as InvoicesBundle;
};

const getPeriodBalance = (
    yearState: BalanceBundleList | null,
    year: number,
    month?: number | null,
) => {
    if (!yearState) return null;

    if (month) return yearState[getPeriodString(year, month)] ?? null;

    return Object.values(yearState).reduce<BalanceBundle<number | null>>(
        (acc, balance) => {
            if (!balance) return acc;

            return {
                accrual: (acc.accrual ?? 0) + (balance.accrual ?? 0),
                refund: (acc.refund ?? 0) + (balance.refund ?? 0),
                saldo: (acc.saldo ?? 0) + (balance.saldo ?? 0),
                payed: (acc.payed ?? 0) + (balance.payed ?? 0),
            };
        },
        {accrual: null, refund: null, saldo: null, payed: null},
    );
};

const fetchBalances = async ({year, month}: ManagementSummaryPeriodParams) => {
    const periods = getPeriods(year);
    const [all, payed] = await Promise.all([
        fetchNullable<BalanceBundleListResponse>(
            api.post(`${FISCAL_PATH}/findSaldoForAll`, {periods}),
        ),
        fetchNullable<BalancePayedResponse>(
            api.post(`${ACCOUNTING_PATH}/api/v2/account/findTaxPayedTotal`, {
                periods,
            }),
        ),
    ]);

    const yearState = all?.monthTaxMap ?? null;

    if (yearState) {
        periods.forEach((period) => {
            const monthBalance = yearState[period];

            if (monthBalance) {
                monthBalance.payed = payed?.periodPayed[period] ?? null;
            } else {
                yearState[period] = null;
            }
        });
    }

    return {
        period: getPeriodBalance(yearState, year, month),
        year: yearState,
    };
};

const fetchGapes = async (params: ManagementSummaryPeriodParams) => {
    const fetchStats = <T>(url: string) =>
        fetchNullable<T>(
            api.get(`${FISCAL_PATH}/gap/aggregate/${params.month ? 'monthly' : 'yearly'}${url}`, {
                params: getPeriodParams(params),
            }),
        );

    const [
        totalCnt,
        totalSum,
        deductionPercent,
        notFiledCnt,
        differCntSum,
        notPaidCntSum,
    ] = await Promise.all([
        fetchStats<GapesCount>('TotalCnt'),
        fetchStats<GapesSum>('TotalSum'),
        fetchStats<GapesDeductionPercent>('GapDeductionPercent'),
        fetchStats<GapesCntSum>('NotFiledCntSum'),
        fetchStats<GapesCntSum>('DifferCntSum'),
        fetchStats<GapesCntSum>('NotPaidCntSum'),
    ]);

    return {
        totalCnt: totalCnt ?? undefined,
        totalSum: totalSum ?? undefined,
        deductionPercent: deductionPercent ?? undefined,
        notFiledCnt: notFiledCnt ?? undefined,
        differCntSum: differCntSum ?? undefined,
        notPaidCntSum: notPaidCntSum ?? undefined,
    } as GapesBundle;
};

export const fetch = async (params: ManagementSummaryPeriodParams): Promise<ManagementSummary> => {
    const [users, declarations, invoices, balances, gapes] = await Promise.all([
        fetchUsers(params),
        fetchDeclarations(params),
        fetchInvoices(params),
        fetchBalances(params),
        fetchGapes(params),
    ]);

    return {
        users,
        declarations,
        invoices,
        balances,
        gapes,
    };
};
